import React from 'react';
import { Card, CardContent } from './ui/card';
import { MapPin, Phone, Mail, Clock, MessageCircle, Building2, CheckCircle2 } from 'lucide-react';

const Contact = ({ data = {} }) => {
  const phone = data.phone || '0522 410 2175';
  const whatsapp = data.whatsapp || '+91-9118946361';
  const whatsappNumber = data.whatsapp_number || '919118946361';
  const email = data.email || 'info@naraintrading.com';

  const enquiryTemplate =
    "Hello Narain Trading Co.,\n\n" +
    "I would like to enquire about your products.\n\n" +
    "Project Details:\n" +
    "• Company/Firm Name: \n" +
    "• Role (Contractor / MEP Consultant / Builder): \n" +
    "• Project Location: \n" +
    "• Required Products (Pipes/Valves): \n" +
    "• Approx Quantity / Size: \n" +
    "• Expected Timeline: \n\n" +
    "Thank you.";

  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(enquiryTemplate)}`;
  const emailLink = `mailto:${email}?subject=${encodeURIComponent("Product Enquiry - Narain Trading Co.")}&body=${encodeURIComponent(enquiryTemplate)}`;

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">{data.section_subtitle || 'Get In Touch'}</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {data.section_title_part1 || 'Request A'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">{data.section_title_part2 || 'Quote'}</span>
          </h3>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-500 to-yellow-600 mx-auto mb-6"></div>
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-yellow-50 border-2 border-yellow-200 text-yellow-900 px-5 py-2.5 rounded-full text-sm font-semibold mb-4">
              <Building2 size={16} />
              <span>{data.audience_badge || 'For Contractors, MEP Consultants & Bulk Buyers'}</span>
            </div>
            <p className="text-gray-600 text-lg">
              {data.intro_text || 'Connect with us directly on WhatsApp or Email with your project details. We respond within 24 hours.'}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
          {/* WhatsApp CTA */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
            data-testid="cta-whatsapp"
          >
            <Card className="border-2 border-green-100 hover:border-green-400 transition-all hover:shadow-2xl h-full overflow-hidden group-hover:-translate-y-1 duration-300">
              <div className="h-2 bg-gradient-to-r from-green-400 to-green-600"></div>
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <MessageCircle className="text-white" size={32} />
                  </div>
                  {data.whatsapp_card_badge && (
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">{data.whatsapp_card_badge}</span>
                  )}
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">{data.whatsapp_card_title || 'Message on WhatsApp'}</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {data.whatsapp_card_description || 'Send us your project requirements directly on WhatsApp.'}
                </p>
                <div className="space-y-2 mb-6">
                  {[data.whatsapp_feature_1, data.whatsapp_feature_2, data.whatsapp_feature_3].filter(Boolean).map((feature, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircle2 size={16} className="text-green-600 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-xs text-green-700 uppercase tracking-wider font-semibold mb-1">WhatsApp</p>
                  <p className="text-green-900 font-bold text-lg">{whatsapp}</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white text-center font-bold py-4 rounded-xl group-hover:from-green-600 group-hover:to-green-700 transition-all">
                  {data.whatsapp_cta_label || 'Open WhatsApp Chat'} →
                </div>
              </CardContent>
            </Card>
          </a>

          {/* Email CTA */}
          <a
            href={emailLink}
            className="group block"
            data-testid="cta-email"
          >
            <Card className="border-2 border-blue-100 hover:border-blue-400 transition-all hover:shadow-2xl h-full overflow-hidden group-hover:-translate-y-1 duration-300">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-700"></div>
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Mail className="text-white" size={32} />
                  </div>
                  {data.email_card_badge && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">{data.email_card_badge}</span>
                  )}
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">{data.email_card_title || 'Email Your Requirement'}</h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {data.email_card_description || 'Best for detailed enquiries with attachments.'}
                </p>
                <div className="space-y-2 mb-6">
                  {[data.email_feature_1, data.email_feature_2, data.email_feature_3].filter(Boolean).map((feature, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircle2 size={16} className="text-blue-600 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-xs text-blue-700 uppercase tracking-wider font-semibold mb-1">Email</p>
                  <p className="text-blue-900 font-bold text-lg break-all">{email}</p>
                </div>
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center font-bold py-4 rounded-xl group-hover:from-blue-700 group-hover:to-blue-900 transition-all">
                  {data.email_cta_label || 'Compose Email'} →
                </div>
              </CardContent>
            </Card>
          </a>
        </div>

        {/* Info Bar */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-white border-2 border-blue-100 rounded-2xl p-8 shadow-md">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-white" size={20} />
                </div>
                <div>
                  <h5 className="font-bold text-gray-900 mb-1">{data.info_visit_title || 'Visit Our Showroom'}</h5>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {data.address_line1 || 'B-93, Vibhuti Khand, Gomti Nagar,'}<br />
                    {data.address_line2 || 'Lucknow, Uttar Pradesh 226010'}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <h5 className="font-bold text-gray-900 mb-1">{data.info_phone_title || 'Direct Line'}</h5>
                  <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-blue-600 hover:text-blue-800 font-semibold block" data-testid="contact-phone">
                    {phone}
                  </a>
                  {data.info_phone_subtitle && (
                    <p className="text-xs text-gray-500 mt-1">{data.info_phone_subtitle}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="text-white" size={20} />
                </div>
                <div>
                  <h5 className="font-bold text-gray-900 mb-1">{data.info_hours_title || 'Business Hours'}</h5>
                  <p className="text-gray-600 text-sm">
                    {data.business_hours_days || 'Monday - Sunday'}<br />
                    {data.business_hours_time || '11:00 AM - 8:00 PM'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {data.lead_quality_note && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 italic">{data.lead_quality_note}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
